import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeCoNumber } from '@/lib/changeOrderUtils';

interface Props {
  open: boolean;
  initialCoNumber: string;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (coNumber: string) => void;
}

export default function CoNumberQuickEditModal({
  open,
  initialCoNumber,
  saving,
  error,
  onClose,
  onSave,
}: Props) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialCoNumber);

  useEffect(() => {
    if (open) setValue(initialCoNumber);
  }, [open, initialCoNumber]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl p-5 max-w-sm w-full shadow-xl">
        <h4 className="text-sm font-bold text-[#1A1A1A] mb-1">{t('co.editCoNumberTitle')}</h4>
        <p className="text-xs text-[#6B7280] mb-4">{t('co.editCoNumberDesc')}</p>
        <label className="text-xs font-semibold text-[#374151] mb-1 block">{t('co.form.coNumberLabel')}</label>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => setValue((v) => (v ? normalizeCoNumber(v) : v))}
          placeholder="CO-001"
          className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] transition-colors mb-2"
          autoFocus
        />
        {error && <p className="text-xs text-[#EF4444] mb-2">{error}</p>}
        <div className="flex gap-2 justify-end mt-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-xl border border-gray-200 text-[#374151] text-xs font-semibold cursor-pointer disabled:opacity-50"
          >
            {t('co.action.cancelConfirm')}
          </button>
          <button
            onClick={() => onSave(value)}
            disabled={saving || !value.trim()}
            className="px-4 py-2 rounded-xl bg-[#E8621A] text-white text-xs font-semibold cursor-pointer disabled:opacity-50"
          >
            {saving ? t('co.saving') : t('co.saveCoNumber')}
          </button>
        </div>
      </div>
    </div>
  );
}
